/**
 * Composer attachment orchestration: file pick → validate → optimistic preview
 * → upload (through the auth seam) → mark ready. Keeps the upload lifecycle in
 * a data-access hook so the Composer component stays declarative.
 *
 * Storage path: the upload endpoint mints the authoritative path server-side
 * AND returns it. We compute a provisional `<localAccountId>/<uuid>.<ext>` path
 * only for the optimistic pre-upload preview; once /upload resolves we overwrite
 * it with the server-minted `path` so the value sent to POST /api/chat/send is
 * guaranteed to match where the bytes actually live (it requires `path` to start
 * with `<userId>/`). The SAS `url` from /upload is kept for previews.
 */
import { useCallback } from 'react';
import { toast } from 'sonner';
import { getActiveAccount } from '@/auth/msal';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import type { PendingAttachment } from '@/features/chat/types';
import { uploadFile, UploadError } from './upload';

/** MIME types the backend (file-processor + upload allow-list) can handle. */
const ALLOWED_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);
/** /upload rejects >10 MiB; cap client-side so the user gets an instant error. */
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 5;

function extensionFor(file: File): string {
  const fromName = file.name.includes('.')
    ? (file.name.split('.').pop() ?? '')
    : '';
  if (fromName) return fromName.toLowerCase();
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'text/plain') return 'txt';
  if (file.type.startsWith('image/')) return file.type.split('/')[1];
  return 'bin';
}

export function useAttachments() {
  const {
    pendingAttachments,
    addPendingAttachment,
    updatePendingAttachment,
    removePendingAttachment,
  } = useSessionStore();

  const addFiles = useCallback(
    async (files: File[]) => {
      const account = getActiveAccount();
      const userId = account?.localAccountId ?? account?.homeAccountId ?? '';

      for (const file of files) {
        const current = useSessionStore.getState().pendingAttachments;
        if (current.length >= MAX_FILES) {
          toast.error(`Maximum ${MAX_FILES} files per message.`);
          break;
        }
        if (!ALLOWED_TYPES.has(file.type)) {
          toast.error(`Unsupported file type: ${file.type || file.name}`);
          continue;
        }
        if (file.size > MAX_FILE_BYTES) {
          toast.error(`File too large: ${file.name} (max 10 MB)`);
          continue;
        }

        const clientId =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;
        const preview = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null;
        // Provisional path for the optimistic preview only. Overwritten with
        // the authoritative server-minted path once /upload resolves below.
        const path = `${userId}/${clientId}.${extensionFor(file)}`;

        const attachment: PendingAttachment = {
          name: file.name,
          type: file.type,
          size: file.size,
          path,
          url: null,
          preview,
          uploading: true,
          clientId,
        };
        addPendingAttachment(attachment);

        try {
          const { url, path: serverPath } = await uploadFile(file);
          // Use the server-minted path — it is where the bytes actually live
          // and is guaranteed to pass /api/chat/send's `<userId>/` check.
          updatePendingAttachment(clientId, {
            url,
            path: serverPath,
            uploading: false,
          });
        } catch (err) {
          removePendingAttachment(clientId);
          if (preview) URL.revokeObjectURL(preview);
          const message =
            err instanceof UploadError
              ? err.message
              : 'Upload failed. Please try again.';
          toast.error(message);
        }
      }
    },
    [addPendingAttachment, updatePendingAttachment, removePendingAttachment],
  );

  const removeAttachment = useCallback(
    (clientId: string) => {
      const target = useSessionStore
        .getState()
        .pendingAttachments.find((a) => a.clientId === clientId);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      removePendingAttachment(clientId);
    },
    [removePendingAttachment],
  );

  const uploading = pendingAttachments.some((a) => a.uploading);

  return { pendingAttachments, addFiles, removeAttachment, uploading };
}
